import _ from "lodash";
import { Dashboard } from "../../gateway/Dashboard/Dashboard";
import { Visualization } from "../../gateway/Visualization/Visualization";
import { HttpAdapter } from "../../main/adapters/HttpAdapter";
import { CompressedJsonAdapter } from "../../presentation/adpters/CompressedJsonAdapter/CompressedJsonAdapter";
import { PopulateManager } from "../../presentation/services/PopulateManager/PopulateManager";
import { Available } from "../Available/Available";
import { Serie } from "../Serie/Serie";
import { PointMap } from "./PointMap";

/** Cenarios sinteticos: nao existe ponto gravado para eles, o valor vem sempre do calculo. */
const CENARIOS_SEM_PONTO_PROPRIO = ["ORIGINAL", "BASE_ZERO", "PROGRESSAO_LINEAR"];
let calculatedSeriePointsMap = new Map();
export class CalculatingMachineV2Gateway {
    HttpAdapter = new HttpAdapter();
    baseUrl = `${publicSettings.aws.baseUrls.dataTimeseries}`;

    constructor() {
        this.compressJsonAdapter = new CompressedJsonAdapter();
        this.Available = new Available();
        this.populateManager = new PopulateManager();
        this.serieCollection = new Serie({
            PopulateManager: this.populateManager
        });
        this.Dashboard = new Dashboard({
            Visualization: new Visualization(),
            Serie: this.serieCollection
        });
    }

    validateInput({ companyData, source, analyzeGroup, analysisPeriod }) {
        if (!companyData) throw new Error(`Missing params: companyData`);
        if (!source) throw new Error(`Missing params: source`);
        if (analyzeGroup && !["sum", "average"].includes(analyzeGroup))
            throw new Error(`Invalid params: analyzeGroup`);
        if (
            analysisPeriod &&
            !["monthly", "quarterly", "semester", "yearly"].includes(
                analysisPeriod
            )
        )
            throw new Error(`Invalid params: analyzeGroup`);
    }

    /**
     * No GRUPO o cenario tem ponto proprio, gravado com `c = groupId`. O consolidado dele nao pode
     * sair da expressao: ela soma as series das EMPRESAS-MEMBRO, e elas nao tem ponto nenhum no
     * cenario do grupo -- o resultado seria zero na tela inteira. `isScenarioView` faz o motor ler
     * o ponto gravado de cada serie; o que e derivado (indicador, percentual) continua sendo
     * calculado em cima desses valores.
     *
     * Medido no grupo SINTESE, fonte Fiscal, cenario "Previsto 2027", com o mesmo input que a tela
     * manda: sem isto, 0 de 15 series com valor; com isto, 11 -- e o Faturamento de janeiro bate
     * com o que esta gravado (R$ 1.574.261,30).
     *
     * Os cenarios sinteticos ficam de fora de proposito: para eles nao existe ponto gravado, e no
     * ORIGINAL o consolidado E a soma das empresas.
     */
    _isGroupScenarioView(companyData, scenarioId) {
        if (!companyData?.isGroup) return false;
        return !CENARIOS_SEM_PONTO_PROPRIO.includes(scenarioId);
    }

    getParans({
        companyData,
        source,
        scenarioId,
        analyzeGroup,
        analysisPeriod,
        isScenarioView
    }) {
        if (!scenarioId) scenarioId = "ORIGINAL";
        if (!analyzeGroup) analyzeGroup = "sum";
        if (!analysisPeriod) analysisPeriod = "monthly";
        const accountingId = companyData.accountingId;
        const companyId = companyData._id;
        const isAnalysisCashFlow =
            source == "fin" &&
            companyData?.relation?.contaazul?.type === "cashFlow";
        const countGroupCompanies = companyData?.companyIds?.length || 0;

        return {
            accountingId,
            companyId,
            source,
            scenarioId,
            analyzeGroup,
            isAnalysisCashFlow,
            analysisPeriod,
            countGroupCompanies,
            isScenarioView:
                isScenarioView ||
                this._isGroupScenarioView(companyData, scenarioId)
        };
    }

    /**
     *
     * @param {
     *  companyData: { _id, accountingId, relation, companyIds },
     *  source: string,
     *  analysisPeriod?: [monthly | quarterly| semester | yearly]
     *  analyzeGroup?: ["sum", "average"]
     *  scenarioId?: string
     *  years number[]
     * } parans
     * @returns
     */
    async calculateSeries({
        companyData,
        source,
        scenarioId,
        analyzeGroup,
        analysisPeriod,
        currentDate,
        startDate,
        currentComparation,
        availableFilter,
        ignoreRaw = false,
        multipleSource = false,
        isScenarioView = false,
        allMonthsByYear = false,
        projection,
        totalMonthsProjection = 12,
        budget = false,
        allYearsAvailable = false,
        goals,
        dashboardId = false,
        goalsIds,
        seriesIds,
        preserveMap = true,
        /**
         * Devolve o mapa de pontos calculado SEM tocar no mapa do modulo.
         *
         * O mapa global e indexado por `serieId -> time -> scenarioId -> period`, sem companyId --
         * e e justamente essa ausencia que faz o consolidado do grupo somar. A consequencia e que
         * calcular a mesma serie para a empresa A e depois para a B grava na mesma chave, e a
         * segunda apaga a primeira. Para exibir valor por empresa nas telas movidas por
         * visualization (DRE Contabil e Balanco), cada empresa precisa do seu proprio mapa.
         */
        isolated = false,
        /**
         * Empresa usada SO para descobrir as datas disponiveis, quando ela e diferente da empresa
         * do calculo.
         *
         * No grupo, o front popula a disponibilidade das membros reetiquetada para o `groupId`
         * (`available-populate.usecase`), entao consultar pelo id da empresa-membro devolve
         * `[null]` -- `timesAvailable` fica vazio e o calculo retorna sem nada. As colunas na tela
         * sao as do grupo de qualquer forma: o que muda por empresa e o valor, nao o periodo.
         */
        availabilityCompanyId = null
    }) {
        this.validateInput({
            companyData,
            source,
            analyzeGroup,
            analysisPeriod
        });
        const parans = this.getParans({
            companyData,
            source,
            scenarioId,
            analyzeGroup,
            analysisPeriod,
            isScenarioView
        });
        try {
            let timesAvailable = [];
            if (startDate) {
                timesAvailable = this.Available.getAvailableByIntervals({
                    companyId: availabilityCompanyId || parans.companyId,
                    source: parans.source,
                    scenarioId: parans.scenarioId,
                    startDate,
                    currentDate,
                    currentComparation,
                    projection,
                    budget
                });
            } else {
                timesAvailable = this.Available.getByFilterDate({
                    companyId: availabilityCompanyId || parans.companyId,
                    source: parans.source,
                    scenarioId: parans.scenarioId,
                    currentDate,
                    currentComparation,
                    availableFilter,
                    analysisPeriod,
                    allMonthsByYear,
                    projection,
                    budget,
                    allYearsAvailable
                });
            }
            timesAvailable = this._sanitizeTimesAvailable(timesAvailable);
            if (!timesAvailable.length) return;
            const input = {
                compressed: true,
                company: {
                    _id: companyData._id,
                    accountingId: companyData.accountingId,
                    companyIds: companyData.companyIds,
                    relation: companyData.relation
                },
                timesAvailable,
                options: {
                    totalGroupCompanies: companyData.companyIds
                        ? companyData.companyIds.length
                        : 0,
                    // Do `parans`, e nao do parametro cru: e la que a regra do cenario de grupo
                    // entra (ver `_isGroupScenarioView`). Lendo a variavel direto, quem nao passa
                    // a marcacao explicitamente -- o dashboard, a DRE, o balanco, os relatorios --
                    // continuava mandando `false` e recebendo zero no cenario do grupo.
                    isScenarioView: parans.isScenarioView,
                    isCashFlow: parans.isCashFlow,
                    scenarioId: parans.scenarioId,
                    period: parans.analysisPeriod,
                    groupFilterOper: parans.analyzeGroup
                }
            };
            if (goals) {
                input.goalsIds = this._getGoalsIds(
                    companyData._id,
                    parans.source,
                    dashboardId,
                    goalsIds
                );
            } else {
                input.seriesIds = _.uniq(
                    this.getSeriesIds({
                        ...parans,
                        ignoreRaw,
                        multipleSource,
                        projection,
                        dashboardId,
                        seriesIds
                    }).filter((s) => s)
                );
            }

            if (projection) {
                if (projection != "PROGRESSAO_LINEAR") {
                    input.options.scenarioId = projection;
                    // A marcacao tem de seguir o cenario que REALMENTE vai na requisicao:
                    // `getParans` avaliou a regra sobre o cenario base, e a projecao troca o
                    // scenarioId aqui. Sem recalcular, a projecao de um grupo saia com o id da
                    // projecao e `isScenarioView: false` -- e voltava zerada.
                    input.options.isScenarioView =
                        input.options.isScenarioView ||
                        this._isGroupScenarioView(companyData, projection);
                } else {
                    input.options.projection = {
                        scenarioId: projection,
                        totalMonths: totalMonthsProjection
                    };
                }
            }
            const calculatedPoints = await this.calculatrixPaginateBySeriesIds(
                input
            );
            const generatedMap = PointMap.generateMap(
                calculatedPoints,
                isolated || !preserveMap ? new Map() : calculatedSeriePointsMap,
                { projection: projection, scenarioIdBase: parans.scenarioId }
            );
            if (isolated) return generatedMap;
            calculatedSeriePointsMap = generatedMap;
        } catch (error) {
            console.error("CalculatingMachineV2Gateway", error);
        }
    }

    _getGoalsIds(companyId, source, dashboardId, goalsIds) {
        if (goalsIds?.length) {
            if (source == "accounting") {
                const ids = this.serieCollection
                    .findCollection(
                        { referenceId: { $in: goalsIds } },
                        {},
                        "array"
                    )
                    .map((g) => g._id);
                return ids;
            } else {
                return goalsIds;
            }
        }
        if (dashboardId) {
            const _golasIds = this.Dashboard.getSeriesInUse(
                dashboardId,
                "goals",
                goalsIds
            );
            if (_golasIds.length) return _golasIds;
        }

        if (source == "accounting") companyId = "EMPRESA_NUCONT";
        const series = this.serieCollection.findCollection(
            {
                sources: source,
                companyId,
                "metadata.origin": "goals"
            },
            {},
            "array"
        );
        return series.map((s) => s._id);
    }

    async calculatrixPaginateBySeriesIds(input) {
        if (input.seriesIds?.length) {
            const seriesIds = input.seriesIds;
            const pointsResponse = [];
            const pageSize = 200;
            const numberOfPages = Math.ceil(seriesIds.length / pageSize);
            const requests = [];
            for (let i = 0; i < numberOfPages; i++) {
                const startIdx = i * pageSize;
                const endIdx = (i + 1) * pageSize;
                const seriesSubset = seriesIds.slice(startIdx, endIdx);
                const requestPromise = this.HttpAdapter.call(
                    `${this.baseUrl}/calculatrix/calculate`,
                    { ...input, seriesIds: seriesSubset }
                );
                requests.push(requestPromise);
            }

            try {
                const responses = await Promise.all(requests);
                responses.forEach((r) => {
                    const calculatedPoints = this._extractCalculatedPoints(r);
                    pointsResponse.push(...calculatedPoints);
                });
            } catch (error) {
                console.error("Erro ao fazer chamadas paginadas:", error);
            }
            return pointsResponse;
        } else if (input.goalsIds?.length) {
            const pointsResponse = await this.HttpAdapter.call(
                `${this.baseUrl}/calculatrix/calculate`,
                input
            );
            return this._extractCalculatedPoints(pointsResponse);
        }

        // Sem serie e sem meta nao ha o que calcular. Cair daqui sem `return` devolvia
        // `undefined`, e o `PointMap.generateMap` estourava com "points is not iterable" -- o
        // dashboard do grupo abria em branco, sem grafico e sem estado vazio, sempre que a fonte
        // escolhida nao tinha serie nenhuma no grupo.
        return [];
    }

    /**
     *
     * @param {*} parans
     * @returns string[]
     */
    getSeriesIds({
        multipleSource,
        companyId,
        source,
        dashboardId,
        ignoreRaw,
        countGroupCompanies,
        projection,
        seriesIds
    }) {
        if (seriesIds?.length) return seriesIds;
        if (dashboardId) {
            const serieIds = this.Dashboard.getSeriesInUse(dashboardId);
            if (source === "accounting") {
                serieIds.push("tAnrDEqRR2hto1ev7");
            }
            if (serieIds.length) return serieIds;
        }
        let selectSeries = {
            companyId: { $in: [companyId, "EMPRESA_NUCONT"] },
            sources: source
        };
        if (ignoreRaw) {
            selectSeries.type = { $ne: "raw" };
        }
        if (countGroupCompanies > 0) {
            selectSeries = {
                $or: [
                    { groupId: companyId },
                    {
                        companyId: {
                            $in: [companyId, "EMPRESA_NUCONT"]
                        }
                    }
                ],
                sources: source
            };
        }
        if (source === "accounting") {
            selectSeries.sources = { $in: ["accounting", "custom"] };
        }
        const taxSources = ["simplesNacional", "tax"];
        const personnelSources = ["personnel", "eSocial"];
        const sourceCobination = [...taxSources, ...personnelSources];
        if (multipleSource && sourceCobination.includes(source)) {
            if (taxSources.includes(source)) {
                selectSeries.sources = {
                    $in:
                        projection === "PROGRESSAO_LINEAR"
                            ? [source]
                            : taxSources
                };
            } else if (personnelSources.includes(source)) {
                selectSeries.sources = {
                    $in:
                        projection === "PROGRESSAO_LINEAR"
                            ? [source]
                            : personnelSources
                };
            }
        }
        const series = this.serieCollection.findCollection(
            selectSeries,
            {},
            "array"
        );
        return series
            .filter((s) => s?.metadata?.origin !== "goals")
            .map((s) => s._id);
    }

    setDrillerPoints(pointsDrillers) {
        calculatedSeriePointsMap = PointMap.generateMap(
            pointsDrillers,
            calculatedSeriePointsMap
        );
    }

    getCalculatedSeriePointsMap() {
        return calculatedSeriePointsMap;
    }

    _sanitizeTimesAvailable(timesAvailable) {
        if (!timesAvailable?.length) return [];
        return timesAvailable.filter((time) => time != null);
    }

    /**
     * A resposta do calculatrix vem como lista de pontos, ou compactada. Qualquer outra coisa e
     * erro do servico (413, 500, timeout), e ai o corpo e um objeto -- espalha-lo com `...`
     * derrubava a pagina em vez de deixar a tela sem ponto.
     */
    _extractCalculatedPoints(response) {
        const points = response?.compressed
            ? this.compressJsonAdapter.decompress(response.response)
            : response;

        if (Array.isArray(points)) return points;

        if (points)
            console.error(
                "CALCULATRIX NAO DEVOLVEU PONTOS",
                response?.message || response
            );

        return [];
    }

    getCalculatedSeriesPoints(
        seriesIds,
        time,
        scenarioId,
        period,
        visualizationAccumulated
    ) {
        const series = this.serieCollection.findCollection(
            {
                _id: { $in: seriesIds }
            },
            {},
            "array"
        );

        return PointMap.findPoints(
            calculatedSeriePointsMap,
            series,
            scenarioId,
            period,
            time,
            visualizationAccumulated
        );
    }

    getCalculatedSeriesPointsInRange(
        serieIds,
        scenarioId,
        period,
        timesBetween,
        visualizationAccumulated
    ) {
        return this.getSeriesPointsInRangeFromMap(
            calculatedSeriePointsMap,
            serieIds,
            scenarioId,
            period,
            timesBetween,
            visualizationAccumulated
        );
    }

    /**
     * Le pontos de um mapa explicito, em vez do mapa do modulo.
     *
     * Serve as telas movidas por visualization (DRE Contabil e Balanco), onde a mesma serie de
     * template e calculada uma vez por empresa: como a chave do mapa nao tem companyId, cada
     * empresa precisa do seu proprio mapa, e a leitura precisa dizer de qual esta lendo.
     */
    getSeriesPointsInRangeFromMap(
        pointsMap,
        serieIds,
        scenarioId,
        period,
        timesBetween,
        visualizationAccumulated
    ) {
        if (!pointsMap) return [];
        const series = this.serieCollection.findCollection(
            {
                _id: { $in: serieIds }
            },
            {},
            "array"
        );

        return PointMap.findPointsInTimeRange(
            pointsMap,
            series,
            scenarioId,
            period,
            timesBetween,
            visualizationAccumulated
        );
    }
}
